import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Button, 
  Input, 
  Popconfirm, 
  message, 
  Tag, 
  Avatar, 
  Card, 
  Typography, 
  Divider,
  Empty,
  Spin
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CommentOutlined,
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime);

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherNoteModal = ({ visible, onClose, teacherId, teacherName }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('token');
  
  // Wrap fetchNotes in useCallback to prevent it from changing on every render
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/teacher-notes/${teacherId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      message.error('Error loading notes');
    } finally {
      setLoading(false);
    }
  }, [teacherId, token]); // Add dependencies that the function uses from component scope
  
  // Wrap handleAddNote in useCallback
  const handleAddNote = useCallback(async () => {
    if (!newNote.trim()) {
      message.warning('Please enter a note');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/teacher-notes`, {
        teacher_id: teacherId,
        note: newNote,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      message.success('Note added successfully');
      setNewNote('');
      fetchNotes();
    } catch (err) {
      console.error('Failed to add note:', err);
      message.error('Error adding note');
    } finally {
      setSubmitting(false);
    }
  }, [newNote, teacherId, token, fetchNotes]); // Add dependencies
  
  // Wrap handleDeleteNote in useCallback
  const handleDeleteNote = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/teacher-notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Note deleted successfully');
      fetchNotes();
    } catch (err) {
      console.error('Failed to delete note:', err);
      message.error('Error deleting note');
    }
  }, [token, fetchNotes]); // Add dependencies
  
  useEffect(() => {
    if (visible) fetchNotes();
  }, [visible, fetchNotes]); // Now fetchNotes is stable between renders because of useCallback
  
  const renderNoteCard = useCallback((item) => {
    return (
      <Card 
        key={item.id}
        style={{ marginBottom: 16, position: 'relative' }}
        size="small"
        bordered
      >
        <Popconfirm
          title="Delete this note?"
          description="This action cannot be undone."
          onConfirm={() => handleDeleteNote(item.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
          placement="left"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            style={{ 
              position: 'absolute', 
              top: 5, 
              right: 5,
              padding: '2px 8px',
              height: 'auto'
            }}
          />
        </Popconfirm>
        
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Avatar 
            icon={<UserOutlined />} 
            style={{ backgroundColor: '#1890ff', marginRight: 12 }} 
            size="large"
          />
          <div style={{ flex: 1, paddingRight: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <Text strong>{item.created_by_name || 'Admin'}</Text>
                <Tag color="blue" style={{ marginLeft: 8 }}>Staff</Tag>
              </div>
              <Text type="secondary" style={{ paddingRight: 16 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {dayjs(item.created_at).fromNow()}
                <span style={{ marginLeft: 8, fontSize: '12px' }}>
                  {dayjs(item.created_at).format('DD MMM YYYY, HH:mm')}
                </span>
              </Text>
            </div>
            
            <Paragraph 
              style={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap',
                background: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px' 
              }}
            >
              {item.note}
            </Paragraph>
          </div>
        </div>
      </Card>
    );
  }, [handleDeleteNote]);
  
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CommentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <span>Notes for {teacherName || 'Teacher'}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Card bordered={false} style={{ marginBottom: 24, background: '#f9f9f9' }}>
        <TextArea
          value={newNote}
          rows={4}
          onChange={e => setNewNote(e.target.value)}
          placeholder="Add a note about this teacher..."
          style={{ marginBottom: 16 }}
        />
        <div style={{ textAlign: 'right' }}>
          <Button 
            type="primary" 
            onClick={handleAddNote} 
            loading={submitting}
            icon={<PlusOutlined />}
          >
            Add Note
          </Button>
        </div>
      </Card>
      
      <Divider orientation="left">
        <Text strong>{notes.length} Note{notes.length !== 1 ? 's' : ''}</Text>
      </Divider>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin tip="Loading notes..." />
        </div>
      ) : notes.length > 0 ? (
        notes.map(item => renderNoteCard(item))
      ) : (
        <Empty 
          description="No notes yet" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          style={{ margin: '24px 0' }}
        />
      )}
    </Modal>
  );
};

export default TeacherNoteModal;